import { CABIN_HERO_VIDEOS, PE_ASSETS, PE_SRP_HERO_TITLE_IMAGES } from '@/constants/flights/peAssets'
import { CabinInterstitialTheme } from '@/types/srp'

export type CabinTabKey = 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST'

/** Domestic-only PE A/B — international PE is live for everyone. */
export const PE_DOM_EXPERIMENT = {
    experimentName: 'PE_DOM_DT',
    cookieName: 'PE_DOM_DT',
    experimentParameter: 'variants',
    controlVariant: 'control',
    testVariant: 'test',
} as const

/**
 * Dev/QA override: when this cookie is set (any truthy value), the interstitial
 * PE / IU nudge ignores the monthly cap and per-search suppression.
 *
 * Example: `document.cookie = 'pe_always_show_upgrade_callout=1; path=/'`
 */
export const PE_ALWAYS_SHOW_UPGRADE_CALLOUT_COOKIE = 'pe_always_show_upgrade_callout'

export const PE_DEFAULT_LOADING_SKELETON_COUNT = 4

export const CABIN_TAB_OPTIONS: { key: CabinTabKey; label: string }[] = [
    { key: 'ECONOMY', label: 'Economy' },
    { key: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
    { key: 'BUSINESS', label: 'Business' },
    { key: 'FIRST', label: 'First Class' },
]

export const CROSS_CABIN_SAME_TYPE_MESSAGE = 'Both onward and return flight must be of same type'

export const PE_NUDGE_BG = '#f5e2ba'

/**
 * Indigo Upfront chrome — ice-blue tokens only.
 * Title / media come from BE upgradeCallout. Caption/benefits use Figma copy.
 */
export const INDIGO_UPFRONT_NUDGE_THEME = {
    captionBg: '#DDE9FF',
    captionText: '#000000',
    bannerGradient: 'linear-gradient(112.89deg, #D5E5FE 17.4%, #F4F8FF 49.92%)',
    bannerScrim: 'linear-gradient(260.16deg, rgba(244, 248, 255, 0) 30.74%, #F4F8FF 71.74%)',
    /** Figma 4709:46309 — photo sits further left than PE (-39). */
    bannerImageLeftPx: -115,
    bannerImageTopPx: 186,
    bannerImageWidthPx: 340,
    bannerImageHeightPx: 226,
    /** Figma 4709:46310 — ice bloom on the photo seam, not the PE gold dissolve. */
    bannerBlend: '#F5F7FC',
    bannerBlendBlurPx: 14.95,
    bannerBlendTopPx: 158,
    bannerBlendWidthPx: 254,
    bannerBlendHeightPx: 55,
    itineraryGradient: 'linear-gradient(170.25deg, #D5E5FE 17.4%, #F4F8FF 49.92%)',
    itineraryUnderlay: 'linear-gradient(268.13deg, rgba(244, 248, 255, 0) 12.52%, #F4F8FF 93.03%)',
    itineraryBorder: '#DAE9FF',
} as const

export const UPGRADE_NUDGE_VARIANT = {
    PREMIUM: 'premium',
    INDIGO: 'indigo',
} as const

/** Full-page loading overlay shown while the fare-change API is in flight. */
export const UPGRADE_OVERLAY_THEME = {
    [UPGRADE_NUDGE_VARIANT.INDIGO]: {
        gradient: 'linear-gradient(180deg, #A8C8FF 0%, #D5E5FE 40%, #EEF5FF 100%)',
        color: '#1A3A6B',
        title: 'Upgrading to Upfront',
        textGradient: 'linear-gradient(141.84deg, #597FF7 5.1%, #233B68 91.68%)',
    },
    [UPGRADE_NUDGE_VARIANT.PREMIUM]: {
        gradient: 'linear-gradient(180deg, #F5C97A 0%, #FFEAD0 40%, #FFF8F0 100%)',
        color: '#7A4E1E',
        title: 'Upgrading to Premium',
        textGradient: 'linear-gradient(141.84deg, #C8860A 5.1%, #7A4E1E 91.68%)',
    },
} as const

export type UpgradeNudgeVariant = (typeof UPGRADE_NUDGE_VARIANT)[keyof typeof UPGRADE_NUDGE_VARIANT]

/**
 * Nudge frequency cap — 31 impressions / calendar month, one per interstitial
 * open and one per itinerary mount. PE and IU keep separate budgets.
 * Same keys/caps as flights-pwa. Per-search session suppression lives in
 * `NUDGE_SUPPRESSION_STORAGE_KEY` (sessionStorage).
 */
export const NUDGE_FREQUENCY_CONFIG = {
    pe: { caps: { month: 31 }, storageKeys: { monthly: 'pe_nudge_monthly' } },
    indigo: { caps: { month: 31 }, storageKeys: { monthly: 'iu_caption_monthly' } },
} as const

export const NUDGE_SUPPRESSION_STORAGE_KEY = 'nudgeSuppression'

export const INDIGO_CAPTION_CONFIG = {
    /** Figma 3456:44755 / 4709:46510 — BE has no caption or benefit ticks. */
    copy: {
        caption: 'Get free meal, extra legroom, baggage & more',
        itineraryBenefits: [
            '5kg extra baggage',
            'Front seat and free meal',
            'Free date change',
        ],
    },
} as const

// Mirrors flights-pwa (mobile): teaser is visible for 5s, then the upgrade
// callout hand-off begins — see usePremiumEconomy.ts.
export const PE_INTRO_NUDGE_DURATION_MS = 5000

/**
 * Shared hand-off duration: nudge fade-out and upgrade-callout expand run
 * in parallel for this many ms (must stay in sync with PEUpgradeCalloutCard
 * `peCalloutExpand` animation).
 */
export const PE_INTRO_TRANSITION_MS = 600

/** @deprecated Prefer PE_INTRO_TRANSITION_MS — kept as an alias for call sites. */
export const PE_NUDGE_FADE_OUT_MS = PE_INTRO_TRANSITION_MS

export const PE_NUDGE_BANNER_COPY_NUDGE =
    'Experience Premium — free seat, meals & comfort'

export const getPeUpgradeCalloutCopy = (formattedAmount: string) =>
    `Fly premium economy for just ${formattedAmount} more!`

export const PE_UPGRADE_CALLOUT_AMOUNT = 4200

export const PE_UPGRADE_CALLOUT_WIDTH_PX = 214

export const PE_UPGRADE_CALLOUT_HEIGHT_PX = 412

export const PE_CALLOUT_SEAM_OVERLAP_PX = Math.round(55 * (236 / 216))

export const PE_HEADER_PATTERN_IMAGE = {
    // Interstitial PE uses the same CDN arc PNG as the SRP hero (Figma 512:11339 mask).
    PREMIUM: PE_ASSETS.srpPatternGold,
    // First Class interstitial — flights-pwa fareClassTheme PNG.
    FIRST: PE_ASSETS.interstitialPatternFirst,
} as const

/**
 * Figma 4094:47138 — gold gradient painted through the arc-pattern mask
 * (not a flat PNG overlay). Opacity lives on the pattern layer.
 *
 * Stops from Figma Selection colors: #FFC77F → #DFA451 (56%) → #825615,
 * with the design's per-stop alphas + layer opacity (31%).
 */
export const PE_HEADER_PATTERN_GRADIENT =
    'linear-gradient(80.56deg, rgba(255, 199, 127, 0) 18.161%, rgba(223, 164, 81, 0.56) 55.785%, rgb(130, 86, 21) 85.155%)'

export const PE_HEADER_PATTERN_OPACITY = 0.45

/**
 * Soft dissolve for the interstitial pattern layer (Figma 512:11339).
 * Same angle/stops as `PE_HEADER_PATTERN_GRADIENT`, but as an alpha mask so the
 * CDN PNG can fade without using the asset itself as mask-image (CORS).
 */
export const PE_HEADER_PATTERN_FADE =
    'linear-gradient(80.56deg, transparent 18.161%, rgba(0, 0, 0, 0.56) 55.785%, #000 85.155%)'

export type SelectedFareCardPatternConfig = {
    /** `image` paints the CDN artwork directly; `mask` tints it with `gradient`. */
    mode: 'image' | 'mask'
    src: string
    opacity: number
    widthPx: number
    heightPx: number
    offsetRightPx: number
    /** Anchor the layer to the card's bottom (gold cabins) or top (First Class). */
    offsetBottomPx?: number
    offsetTopPx?: number
    /** Maps to background-size / background-position, or mask-* in `mask` mode. */
    backgroundSize: string
    backgroundPosition: string
    /** Figma paints these layers flipped on Y. */
    scaleY: 1 | -1
    /** Gradient-only dissolve, applied in card space outside the Y-flip. */
    fade?: string
    filter?: string
    /** `mask` mode only — the wash painted through the artwork's strokes. */
    gradient?: string
}

/**
 * Figma 4401:47734 — the gold wash painted through the arc motif, clear at the
 * layer's bottom-left and fully opaque past 55%, which puts the deep end of the
 * ramp in the card's top-right corner.
 */
export const PE_FARE_CARD_PATTERN_GRADIENT =
    'linear-gradient(50.25deg, rgba(255, 199, 127, 0) 20.378%, rgba(223, 164, 81, 0.56) 45.108%, #825615 54.931%)'

/**
 * Selected fare-card pattern for Premium Economy / Business (Figma 4401:47698).
 *
 * A top-right layer that overhangs the shell right and above, so the arcs sweep
 * out of the top corner and dissolve before the baggage rows.
 *
 * Uses `mask` mode so the arcs take the gradient's colour, which reaches #825615.
 * Painting the CDN PNG directly and tinting it with a filter cannot get there —
 * the asset's own strokes are light, so they stay peach however they are warmed.
 * Cross-origin `mask-image` is safe here: it is an image reference, not a fetch
 * requiring CORS (verified against an origin serving no Access-Control headers).
 */
export const PE_FARE_CARD_PATTERN: SelectedFareCardPatternConfig = {
    mode: 'mask',
    src: PE_ASSETS.srpPatternGold,
    gradient: PE_FARE_CARD_PATTERN_GRADIENT,
    opacity: 0.31,
    // Figma 4401:47734 — 327.8×418 layer at left 15.09 / top -154.5 on a 220px card.
    widthPx: 328,
    heightPx: 418,
    offsetRightPx: -123,
    offsetTopPx: -155,
    backgroundSize: '326.059px 460.771px',
    backgroundPosition: '0.912px 0px',
    // This layer is not flipped (unlike the earlier bottom-right treatment).
    scaleY: 1,
}

/**
 * Figma 1724:71747 "Pattern 3" — champagne wash painted through the diamond
 * lattice, opaque at the layer's own bottom-right and clear by 81%.
 * The Y-flip lands that opaque end in the card's TOP-right corner.
 */
export const FIRST_FARE_CARD_PATTERN_GRADIENT =
    'linear-gradient(-28.09deg, #CCA771 63.17%, rgba(250, 238, 205, 0) 81.06%)'

/**
 * Figma 1701:85133 — First Class takes the diamond lattice in the TOP-right, not
 * the bottom-right arcs the gold cabins use. The lattice SVG is a mask over
 * `FIRST_FARE_CARD_PATTERN_GRADIENT` rather than flat artwork, so the motif fades
 * with the gradient instead of reading at full strength across the corner.
 *
 * Geometry is the design's layer box (435×445 at right -321, top -212) measured
 * against its 216px card; it overhangs the shell far right and above, so all but
 * the top-right corner is clipped away.
 */
export const FIRST_FARE_CARD_PATTERN: SelectedFareCardPatternConfig = {
    mode: 'mask',
    src: PE_ASSETS.srpPatternFirst,
    gradient: FIRST_FARE_CARD_PATTERN_GRADIENT,
    opacity: 0.12,
    widthPx: 435,
    heightPx: 445,
    offsetRightPx: -321,
    offsetTopPx: -212,
    backgroundSize: '532.374px 317.211px',
    backgroundPosition: '-48.134px 63.918px',
    scaleY: -1,
}

/**
 * Figma 1724:71598 (Pattern 3 / image 31) — soft champagne painted through the
 * First Class diamond mask. Per-stop alphas bake in the design's 12% layer
 * opacity so the motif stays muted even if the wrapper opacity is ignored.
 *
 * Interstitial First Class uses the mobile PNG (`interstitialPatternFirst`) as a
 * direct image overlay — same asset as flights-pwa fareClassTheme.
 */
export const FIRST_HEADER_PATTERN_GRADIENT =
    'linear-gradient(112.71deg, rgba(204, 167, 113, 0.12) 42.954%, rgba(250, 238, 205, 0) 67.035%)'

/** Layer opacity kept at 1 — fade lives in the gradient stops above. */
export const FIRST_HEADER_PATTERN_OPACITY = 1

/** Top header fill for PE / Business interstitial + itinerary modify-fare headers. */
export const PE_HEADER_BG_GRADIENT =
    'linear-gradient(210.19deg, #E8C591 0%, rgb(255, 248, 237) 73.875%)'

export type PeHeaderPatternConfig = {
    src: string
    opacity: number
    /**
     * When set, PE/Business use the Figma right-corner layer (background-image).
     * The gradient value is only a flag — header fill comes from `modalHeaderBg`.
     */
    gradient?: string
    /** Figma Pattern frame size (512:11339 / 4094:50333). */
    layerWidthPx?: number
    /**
     * When set (e.g. First Class `55%`), the pattern layer spans this share of the
     * header from the right so it reaches mid-header on any modal width.
     */
    layerWidthPercent?: number
    layerHeightPx?: number
    /** Distance past the header's right edge (Figma overhang ~18px). */
    layerOffsetRightPx?: number
    layerOffsetTopPx?: number
    /** CSS background-size — Figma mask-size 462×653. */
    backgroundSize?: string
    /** CSS background-position — Figma mask-position 223px 0. */
    backgroundPosition?: string
    /** Gradient-only CSS mask so the pattern dissolves (Figma 512:11339). */
    fade?: string
}

export const PE_HEADER_PATTERN_CONFIG: Record<
    NonNullable<CabinInterstitialTheme['headerPatternVariant']>,
    PeHeaderPatternConfig
> = {
    premium: {
        // Figma 512:11339 — 686×592 layer; mask-size 462×653 at 223px 0.
        // Opacity bumped slightly above Figma's 31% so the arcs read darker on desktop.
        src: PE_HEADER_PATTERN_IMAGE.PREMIUM,
        opacity: PE_HEADER_PATTERN_OPACITY,
        gradient: PE_HEADER_PATTERN_GRADIENT,
        layerWidthPx: 686,
        layerHeightPx: 592,
        layerOffsetRightPx: -18,
        layerOffsetTopPx: -302,
        backgroundSize: '462px 653px',
        backgroundPosition: '223px 0',
        fade: PE_HEADER_PATTERN_FADE,
    },
    first: {
        // First Class interstitial — span right→mid and dissolve toward center
        // (fixed 451px was only ~20% of the desktop modal).
        src: PE_HEADER_PATTERN_IMAGE.FIRST,
        opacity: 1,
        layerWidthPercent: 55,
        layerOffsetRightPx: 0,
        layerOffsetTopPx: 0,
        backgroundSize: '100% auto',
        backgroundPosition: 'right center',
        // Opaque on the right edge; clear by the layer's left (≈ mid-header).
        fade: 'linear-gradient(to left, #000 0%, #000 42%, rgba(0, 0, 0, 0.45) 72%, transparent 100%)',
    },
}

const DEFAULT_INTERSTITIAL_THEME: CabinInterstitialTheme = {
    modalHeaderBg: '#f4f8ff',
    activeTabBorderColor: '#B3B3B3',
    // Same grey as the active tab border — PE/Business use matching golds for
    // tab + full-width rule; keep Economy consistent with that approach.
    tabDividerColor: '#B3B3B3',
    headerTitleColor: '#1A1A1A',
    inactiveTabTextColor: '#808080',
}

const PREMIUM_CABIN_INTERSTITIAL_THEME: CabinInterstitialTheme = {
    // Shared by Premium Economy + Business (interstitial + itinerary modify fare).
    modalHeaderBg: PE_HEADER_BG_GRADIENT,
    activeTabBorderColor: '#E8C983',
    tabDividerColor: '#E4C174',
    // Figma 512:11446 / flights-pwa fareClassTheme — champagne highlight through gold.
    fareCardBorderGradient: 'linear-gradient(to right, #E4C174 0%, #FFF7D8 50%, #D2B277 100%)',
    showHeaderPattern: true,
    headerPatternVariant: 'premium',
    selectedFareCardBackground: 'linear-gradient(-74.35deg, #FFFFFF 60.57%, #E9C387 152.99%)',
}

const INDIGO_UPFRONT_INTERSTITIAL_THEME: CabinInterstitialTheme = {
    modalHeaderBg: '#EEF4FF',
    activeTabBorderColor: '#597FF7',
    tabDividerColor: '#597FF7',
    fareCardBorderGradient: 'linear-gradient(to right, #597FF7 0%, #C3D4FF 50%, #597FF7 100%)',
    selectedFareCardBackground: 'linear-gradient(-74.35deg, #FFFFFF 60.57%, #C3D4FF 152.99%)',
    badgeBackground: '#597FF7',
}

const FIRST_CLASS_INTERSTITIAL_THEME: CabinInterstitialTheme = {
    modalHeaderBg: 'linear-gradient(-0.48deg, #080502 50.04%, #B78754 172%)',
    activeTabBorderColor: '#4C341D',
    tabDividerColor: 'rgba(255, 255, 255, 0.15)',
    fareCardBorderGradient:
        'linear-gradient(to right, #472E17 10%, #FFF7D8 70%, #4F341A 100%)',
    showHeaderPattern: true,
    headerPatternVariant: 'first',
    headerTitleColor: '#FFFFFF',
    selectedFareCardBackground:
        'linear-gradient(-81.32deg, #FFFFFF 60.18%, #DBC3AC 115.72%)',
}

/**
 * Value Max benefits strip on themed cabin tabs — Figma 4143:78063. The default
 * strip is transparent over the card fill; Premium Economy / Business / First
 * Class get their own cream surface with pill chips. Economy is unchanged.
 */
export const PE_VALUE_MAX_BENEFITS_BACKGROUND = '#FFF8ED'
export const PE_VALUE_MAX_BENEFITS_CHIP_RADIUS = '32px'
export const PE_VALUE_MAX_BENEFITS_RADIUS = '16px'

export const isPremiumCabinTab = (cabin: CabinTabKey): boolean =>
    cabin === 'PREMIUM_ECONOMY' || cabin === 'BUSINESS'

export const isFirstClassCabinTab = (cabin: CabinTabKey): boolean => cabin === 'FIRST'

/**
 * Theme lookups must use this cabin key. Control (`isPeTestVariant === false`)
 * always resolves to ECONOMY so PREMIUM_ECONOMY / BUSINESS / FIRST themes never apply.
 */
export const resolveCabinForTheme = (
    cabin?: string | null,
    isPeTestVariant = false,
): string => {
    if (!isPeTestVariant) return 'ECONOMY'
    return cabin || 'ECONOMY'
}

export const getCabinInterstitialTheme = (cabin: CabinTabKey): CabinInterstitialTheme => {
    if (isFirstClassCabinTab(cabin)) return FIRST_CLASS_INTERSTITIAL_THEME
    if (isPremiumCabinTab(cabin)) return PREMIUM_CABIN_INTERSTITIAL_THEME
    if (cabin === 'INDIGO_UPFRONT') return INDIGO_UPFRONT_INTERSTITIAL_THEME
    return DEFAULT_INTERSTITIAL_THEME
}

export const PE_EXPERIENCE_CARD_COPY =
    'Upgrade your travel experience, fly premium!'

export const PE_EXPERIENCE_CARD_IMAGE = PE_ASSETS.promo

export const PE_EXPERIENCE_CARD_WIDTH_PX = 236

export const FARE_CARD_GAP_PX = 16

export const FARE_CARD_STRIDE_PX = PE_EXPERIENCE_CARD_WIDTH_PX + FARE_CARD_GAP_PX

/** Zero-based slot the tile is frozen at (i.e. the 4th column). */
export const PE_EXPERIENCE_CARD_INSERT_INDEX = 3

export const PE_EXPERIENCE_CARD_BACKGROUND =
    'linear-gradient(-74.35deg, #FFFFFF 60.57%, #E9C387 152.99%)'

export const PE_EXPERIENCE_CARD_TOP_SCRIM =
    'linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)'

export const PE_INTRO_PHASE = {
    NUDGE: 'nudge',
    EXITING: 'exiting',
    UPGRADE: 'upgrade',
    // Intro already played once this session — render neither nudge nor callout
    // (mirrors flights-pwa's once-per-session banner latch).
    SUPPRESSED: 'suppressed',
} as const

export type PeIntroPhase = (typeof PE_INTRO_PHASE)[keyof typeof PE_INTRO_PHASE]

export const PE_ITINERARY_UPGRADE_AMOUNT = 4200

export const getPeItineraryUpgradeTitle = (formattedAmount: string) =>
    `Get Premium benefits for just ${formattedAmount} more!`

/** Static benefit rows — no date-change price (that must come from BE when known). */
export const PE_ITINERARY_UPGRADE_BENEFITS: readonly string[] = [
    'Better seats, Priority boarding',
    'Complimentary meals',
]

/**
 * Build upgrade-banner benefits.
 * Date-change is appended only when BE supplies a fee on the upgrade callout —
 * never fall back to a hardcoded ₹ amount.
 */
export const getPeItineraryUpgradeBenefits = (dateChangeFee?: number | null): readonly string[] => {
    if (typeof dateChangeFee === 'number' && dateChangeFee >= 0) {
        const formatted = dateChangeFee.toLocaleString('en-IN')
        return [...PE_ITINERARY_UPGRADE_BENEFITS, `Date change at ₹${formatted}`]
    }
    return PE_ITINERARY_UPGRADE_BENEFITS
}

export const PE_ITINERARY_UPGRADE_SEAT_IMAGE = 'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/images/premiumEconomy/pe-itinerary-upgrade-seat.png'
export const PE_ITINERARY_UPGRADE_BANNER_GRADIENT = 'linear-gradient(-40.75deg, #DFB16D 28.48%, #FFFFFF 83.42%)'
export const PE_ITINERARY_UPGRADE_BANNER_BORDER = '#DEAE6A'

/**
 * Soft left-edge treatment for upgrade media (itinerary banner + interstitial
 * callout). Keep the dissolve tight so the cabin video stays clear; a light
 * blur on the gold underlay hides the hard crop line.
 */
export const PE_UPGRADE_MEDIA_LEFT_MASK =
    'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.45) 4%, #000 11%)'
export const PE_UPGRADE_MEDIA_LEFT_UNDERLAY =
    'linear-gradient(90deg, #E0B371 0%, rgba(224,179,113,0.9) 30%, rgba(224,179,113,0) 100%)'
export const PE_UPGRADE_MEDIA_LEFT_UNDERLAY_WIDTH_PX = 28
export const PE_UPGRADE_MEDIA_LEFT_UNDERLAY_BLUR_PX = 8
/**
 * Top-edge dissolve for the same media. The blurred gold strip alone cannot hide
 * the crop line — it is painted over the media, so its own soft bottom edge lets
 * the hard edge show through. Fading the media's own alpha removes the edge
 * entirely, and the strip then reads as the designed gold bloom (Figma 4094:43563).
 */
export const PE_UPGRADE_MEDIA_TOP_MASK =
    'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.25) 9%, rgba(0,0,0,0.75) 18%, #000 28%)'
// SRP cabin theme: Economy → none, Premium Economy & Business → gold hero,
// First → dark hero. All themed cabins share the gold fare-tuple border.

// Display fonts are not bundled; the stacks fall back to a bundled serif.
export const PE_SRP_HERO_TITLE_FONT_FAMILY =
    "'Boston Angel', 'Tiempos Headline', Georgia, 'Times New Roman', serif"

export const SRP_HERO_FIRST_TITLE_FONT_FAMILY =
    "'Swirly Canalope PERSONAL USE', 'Boston Angel', 'Tiempos Headline', Georgia, serif"

export type SrpHeroPattern = {
    // 'image' draws the SVG directly; 'mask' tints it via `color`.
    kind: 'image' | 'mask'
    src: string
    color?: string
    size: string
    repeat: string
    position: string
    opacity: number
    fade?: string
    blur?: number
}

export type SrpHeroTheme = {
    title: string
    /** CDN SVG title wordmark — preferred over live text for cross-device glyph fidelity. */
    titleImage: string
    titleImageWidth: number
    titleImageHeight: number
    background: string
    overlay?: string
    titleGradient: string
    titleFontFamily: string
    titleFontSize: string
    titleLineHeight: number | string
    titleFontFeatureSettings?: string
    // Prepend the loaded script webfont (First class). Kept for fallback text path.
    scriptTitle?: boolean
    // Tint band at the top that persists (fixed) once the hero scrolls away.
    scrollShade: string
    tupleBorderGradient: string
    /** Top benefit banner on themed SRP tuples (e.g. "Partial Refundable"). */
    tupleBenefitTagBg: string
    tupleBenefitTagTextColor: 'primary' | 'neutral'
    pattern?: SrpHeroPattern
    heroImage: string
    heroImageWidth: number
    heroImageHeight: number
    heroImageTop: number
    borderRadius: number
    // Muted, looping cabin hero video that lazily fades in over `heroImage`.
    heroVideo?: string
}

const SRP_HERO_GOLD_THEME: Omit<SrpHeroTheme, 'title' | 'titleImage' | 'titleImageWidth' | 'titleImageHeight'> = {
    background: 'linear-gradient(221.09deg, #FFE8C8 19.92%, #FFFFFF 89.84%)',
    overlay:
        'linear-gradient(180.54deg, rgba(211, 156, 74, 0.6) 4.8%, rgba(250, 238, 205, 0) 62.83%)',
    titleGradient: 'linear-gradient(144.95deg, #E69E33 2.04%, #673F0B 90.83%)',
    titleFontFamily: PE_SRP_HERO_TITLE_FONT_FAMILY,
    titleFontSize: '64px',
    titleLineHeight: 1.2,
    scrollShade:
        'linear-gradient(200.37deg, #FFF6EA 15.42%, rgba(255, 255, 255, 0) 22.63%)',
    tupleBorderGradient:
        'linear-gradient(135deg, #E4C174 0%, #FFF7D8 50%, #D2B277 100%)',
    tupleBenefitTagBg: '#FFF3DE',
    tupleBenefitTagTextColor: 'primary',
    pattern: {
        // CDN pe-header-pattern.png as background-image (flights-pwa).
        // Figma 762:61453 — motif stays on the LEFT only (opacity ~34%) and
        // dissolves well before the cabin photo; never tiles across the hero.
        kind: 'image',
        src: PE_ASSETS.srpPatternGold,
        color: '#D39C4A',
        // Scale up so the circular arc motifs read clearly behind the title
        // (Figma 762:61453 mask tile is ~807×628).
        size: 'auto 780px',
        repeat: 'no-repeat',
        position: 'left -80px top -220px',
        opacity: 0.4,
        // A bit past mid-left so the motif breathes without reaching the cabin photo.
        fade:
            'linear-gradient(to right, ' +
            'rgba(0,0,0,1) 0%, rgba(0,0,0,0.65) 16%, rgba(0,0,0,0.28) 32%, ' +
            'rgba(0,0,0,0.08) 44%, rgba(0,0,0,0) 54%)',
    },
    heroImage: PE_ASSETS.srpHeroSeat,
    heroImageWidth: 640,
    heroImageHeight: 427,
    heroImageTop: -110,
    borderRadius: 16,
    // Business overrides this with its own cabin video in getSrpHeroTheme.
    heroVideo: CABIN_HERO_VIDEOS.premium,
}

const SRP_HERO_FIRST_THEME: Omit<SrpHeroTheme, 'title' | 'titleImage' | 'titleImageWidth' | 'titleImageHeight'> = {
    background: 'linear-gradient(90deg, #000000 0%, rgba(0,0,0,0) 55%), #1A1A1A',
    titleGradient:
        'linear-gradient(146.86deg, #C06700 4.42%, #EED9B6 39.8%, #FFFAF1 89.1%)',
    titleFontFamily: SRP_HERO_FIRST_TITLE_FONT_FAMILY,
    titleFontSize: '72px',
    titleLineHeight: '90%',
    titleFontFeatureSettings: "'liga' off, 'clig' off",
    scriptTitle: true,
    scrollShade:
        'linear-gradient(200.37deg, #DCC3AC 15.42%, rgba(255, 255, 255, 0) 22.63%)',
    tupleBorderGradient:
        'linear-gradient(135deg, #472E17 0%, #FFF7D8 50%, #4F341A 100%)',
    tupleBenefitTagBg: '#4E351E',
    tupleBenefitTagTextColor: 'neutral',
    // Figma 4094:60397 / 4094:60425 — Pattern 3: diamond SVG is a MASK over a
    // champagne gradient that dissolves rightward (112.71deg, clear by ~67%).
    // Drawing the SVG as a flat `background-image` kept diamonds full-strength
    // across the left half; mask mode matches Figma and fades with the gradient.
    // Size is slightly under Figma's 1056×629 so the lattice reads smaller on desktop.
    pattern: {
        kind: 'mask',
        src: PE_ASSETS.srpPatternFirst,
        color: 'linear-gradient(112.71deg, #CCA771 42.95%, rgba(250, 238, 205, 0) 67.03%)',
        size: '860px 512px',
        repeat: 'no-repeat',
        // Pattern 3 sits up-and-left of the title (Figma x:-116 y:-353 on a 203px header).
        position: 'left -100px top -280px',
        opacity: 0.12,
        // Extra horizontal dissolve so the lattice clears before the cabin photo.
        fade:
            'linear-gradient(to right, ' +
            'rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 22%, rgba(0,0,0,0.28) 38%, ' +
            'rgba(0,0,0,0.06) 48%, rgba(0,0,0,0) 56%)',
    },
    heroImage: PE_ASSETS.srpHeroFirst,
    heroImageWidth: 780,
    heroImageHeight: 426,
    heroImageTop: -70,
    borderRadius: 24,
    heroVideo: CABIN_HERO_VIDEOS.first,
}

export const isSrpPremiumThemeCabin = (cabinClass?: string): boolean => {
    const normalized = (cabinClass || '').toUpperCase()
    return (
        normalized === 'PREMIUM ECONOMY' ||
        normalized === 'PREMIUM_ECONOMY' ||
        normalized === 'BUSINESS' ||
        normalized === 'BUSINESS CLASS'
    )
}

export const isSrpFirstClassCabin = (cabinClass?: string): boolean => {
    const normalized = (cabinClass || '').toUpperCase()
    return normalized === 'FIRST' || normalized === 'FIRST CLASS'
}

export const isSrpThemeCabin = (cabinClass?: string): boolean =>
    isSrpPremiumThemeCabin(cabinClass) || isSrpFirstClassCabin(cabinClass)

export const getSrpHeroTheme = (cabinClass?: string): SrpHeroTheme | null => {
    if (isSrpFirstClassCabin(cabinClass)) {
        return {
            ...SRP_HERO_FIRST_THEME,
            title: 'Fly First Class',
            // SVG viewBox 336×54; slight upscale keeps presence vs old 72px script title.
            titleImage: PE_SRP_HERO_TITLE_IMAGES.first,
            titleImageWidth: 448,
            titleImageHeight: 72,
        }
    }
    if (isSrpPremiumThemeCabin(cabinClass)) {
        const normalized = (cabinClass || '').toUpperCase()
        const isBusiness =
            normalized === 'BUSINESS' || normalized === 'BUSINESS CLASS'
        return {
            ...SRP_HERO_GOLD_THEME,
            title: isBusiness ? 'Fly Business' : 'Fly Premium',
            titleImage: isBusiness
                ? PE_SRP_HERO_TITLE_IMAGES.business
                : PE_SRP_HERO_TITLE_IMAGES.premium,
            // SVG viewBox sizes: Premium 322×60, Business 307×60.
            titleImageWidth: isBusiness ? 307 : 322,
            titleImageHeight: 60,
            // Business reuses the gold chrome but plays its own cabin video.
            heroVideo: isBusiness ? CABIN_HERO_VIDEOS.business : CABIN_HERO_VIDEOS.premium,
        }
    }
    return null
}

export const PE_TUPLE_SELECTED_BORDER_COLOR = '#1A1A1A'

/**
 * Tuple cards are br-12 with a 1px border, so a child pinned to a top corner
 * (e.g. the benefit tag top-left, nearby-airport tag top-right) must round to
 * 11px or it squares off the card's corner.
 */
export const TUPPLE_INNER_RADIUS = 11

/** Solid gold border on the white RT flight card (Figma 562:43915 / mobile PE_CARD_BORDER). */
export const PE_TUPLE_CARD_BORDER_COLOR = '#E4C174'

/**
 * Backing shade for the round-trip price strip on themed SRP tuples. Painted on the
 * OUTER shell so the white flight card can keep a full br-12 and form a curved
 * junction with the strip (Figma 562:43915 / flights-pwa RtTuppleFfExpView).
 */
export const PE_RT_CALLOUT_SHADE = 'linear-gradient(88.76deg, #F4DAB3 0.26%, #FEF8F0 98.75%)'
export const PE_RT_CALLOUT_SHADE_SELECTED = 'linear-gradient(89.91deg, #E4BB7C 0%, #FAEBD4 99.9%)'
export const FC_RT_CALLOUT_SHADE = 'linear-gradient(89.91deg, #532805 0%, #372305 99.9%)'

export const PE_SRP_GLASS_FIELD_CSS = {
    background: 'rgba(255, 255, 255, 0.75)',
    backdropFilter: 'blur(4.45px)',
    WebkitBackdropFilter: 'blur(4.45px)',
} as const
export const normalizeCabinType = (cabinType?: string): string =>
    (cabinType || '').trim().toUpperCase().replace(/\s+/g, '_')

export const ITIN_GOLD_THEME_CABINS: CabinTabKey[] = ['PREMIUM_ECONOMY', 'BUSINESS']

export const isItinGoldThemedCabin = (cabinType?: string): boolean =>
    ITIN_GOLD_THEME_CABINS.includes(normalizeCabinType(cabinType) as CabinTabKey)

export const PE_ITINERARY_HEADER = {
    HEIGHT: 137,
    LAYER_HEIGHT: 240,
    BORDER_RADIUS: 16,
    BASE_GRADIENT: 'linear-gradient(245deg, #FFEAD0 8%, #FFFFFF 80%)',
    OVERLAY_TOP: 'linear-gradient(238deg, rgba(211, 156, 74, 0.55) 0%, rgba(250, 238, 205, 0) 46%)',
    OVERLAY_RIGHT: 'linear-gradient(255deg, rgba(233, 199, 147, 0.8) 4%, rgba(233, 199, 147, 0) 92%)',
    BOTTOM_FADE: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)',
    BOTTOM_FADE_HEIGHT: 125,
    /**
     * Figma 1620:69483 — the same arc motif the SRP hero / fare cards use, mirrored on
     * X and tiled across the full width, cropped to the band the 137px header exposes.
     * The design lays it down as 404×628 images (`Group 1171275430` holds two side by
     * side); 807px was the width of that *pair*, which stretched every column ~1.8x.
     */
    PATTERN_IMAGE: PE_ASSETS.srpPatternGold,
    PATTERN_OPACITY: 0.24,
    PATTERN_SIZE: '446.4px 631px',
    /**
     * Our PNG still carries the ~21px of blank margin either side that the design's
     * copy was trimmed of, so `repeat-x` pinches a half-column into every seam.
     * Stacking no-repeat layers one column-span apart overlaps those margins instead
     * and keeps the lattice even. Eight spans cover 3232px — wider than any desktop.
     */
    PATTERN_TILE_SPAN: 404,
    PATTERN_TILE_COUNT: 8,
    PATTERN_TOP: -290,
    PATTERN_LAYER_HEIGHT: 569,
} as const

export const FIRST_CLASS_ITINERARY_HEADER = {
    HEIGHT: 160,
    BORDER_RADIUS: 16,
    BASE_GRADIENT: 'linear-gradient(262deg, #734B25 0%, #DCC3AC 34%, #FFFFFF 68%)',
    BOTTOM_FADE: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)',
    BOTTOM_FADE_HEIGHT: 155,
    PATTERN_IMAGE: PE_ASSETS.itineraryPatternFirst,
    PATTERN_OPACITY: 0.1,
    PATTERN_WIDTH: '1000px',
    PATTERN_HEIGHT: '625px',
    PATTERN_FADE: 'linear-gradient(to left, #000 0%, #000 20%, rgba(0, 0, 0, 0) 90%)',
} as const

export type ItinHeaderThemeKey = 'gold' | 'first'

export const isFirstClassItinCabin = (cabinType?: string): boolean => {
    const cabin = normalizeCabinType(cabinType)
    return cabin === 'FIRST' || cabin === 'FIRST_CLASS'
}

export interface ItinDiscoveryBannerConfig {
    HEIGHT: number
    TITLE: string
    BG_GRADIENT: string
    TITLE_GRADIENT: string | null
    TITLE_COLOR: string
    SEAT_IMAGE: string
    SEAT_IMAGE_WIDTH: string
    SEAT_IMAGE_FADE: string
    PATTERN_IMAGE?: string
    PATTERN_GRADIENT?: string
    PATTERN_OPACITY?: number
    PATTERN_WIDTH?: string
    PATTERN_HEIGHT?: string
    PATTERN_LEFT?: string
    PATTERN_TOP?: string
    PATTERN_BACKGROUND_SIZE?: string
    PATTERN_BACKGROUND_POSITION?: string
    /** Figma paints the layer mirrored on X (rotate 180 + flip Y). */
    PATTERN_FLIP_X?: boolean
}

export const PE_ITINERARY_DISCOVERY_BANNER: ItinDiscoveryBannerConfig = {
    HEIGHT: 104,
    TITLE: 'Step inside & discover a new standard of flight',
    BG_GRADIENT: 'linear-gradient(25.85deg, #FFFFFF 11.29%, #D7BA83 93.59%)',
    TITLE_GRADIENT: 'linear-gradient(219.68deg, #9E7847 9.16%, #DEAF68 104.36%)',
    TITLE_COLOR: '#9E7847',
    SEAT_IMAGE: 'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/images/premiumEconomy/pe-itinerary-discovery-seat.jpg',
    SEAT_IMAGE_WIDTH: '62%',
    SEAT_IMAGE_FADE: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 8%, #000 16%)',
    // Figma 1620:69570 — the shared arc motif, mirrored, sitting high enough that only
    // its lower arcs reach into the 104px banner. Gradient is the design's fill
    // (#A67329 → transparent) re-expressed as an alpha mask over the CDN PNG.
    PATTERN_IMAGE: PE_ASSETS.srpPatternGold,
    PATTERN_GRADIENT:
        'linear-gradient(255.65deg, #000 27.74%, rgba(0, 0, 0, 0.56) 64.49%, rgba(0, 0, 0, 0) 75.51%)',
    PATTERN_OPACITY: 0.24,
    PATTERN_WIDTH: '369px',
    PATTERN_HEIGHT: '289px',
    PATTERN_LEFT: '-11px',
    PATTERN_TOP: '-195px',
    PATTERN_BACKGROUND_SIZE: '410px 320px',
    PATTERN_BACKGROUND_POSITION: '12px -1px',
    PATTERN_FLIP_X: true,
}

export const FIRST_CLASS_ITINERARY_DISCOVERY_BANNER: ItinDiscoveryBannerConfig = {
    HEIGHT: 104,
    TITLE: 'Step inside & discover a new standard of flight',
    BG_GRADIENT: 'linear-gradient(49.09deg, #1A1006 8.46%, #53361B 70.07%, #ECD6B9 109.5%)',
    TITLE_GRADIENT: null,
    TITLE_COLOR: '#FFFFFF',
    SEAT_IMAGE: 'https://rukmini-ct.flixcart.com/f_auto,q_auto/offermgmt-prod/offermgmt/images/premiumEconomy/first-class-itinerary-discovery-seat.jpg',
    SEAT_IMAGE_WIDTH: '62%',
    SEAT_IMAGE_FADE: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.85) 8%, #000 16%)',
    PATTERN_IMAGE: PE_ASSETS.itineraryPatternFirst,
    PATTERN_GRADIENT: 'linear-gradient(125.69deg, #CCA771 1%, rgba(250, 238, 205, 0) 61.02%)',
    PATTERN_OPACITY: 0.15,
    PATTERN_WIDTH: '80%',
    PATTERN_HEIGHT: '380px',
}

export const getItineraryDiscoveryBanner = (theme: ItinHeaderThemeKey): ItinDiscoveryBannerConfig =>
    theme === 'first' ? FIRST_CLASS_ITINERARY_DISCOVERY_BANNER : PE_ITINERARY_DISCOVERY_BANNER

export const PE_ITINERARY_GOLD_BORDER = {
    COLOR: '#E8C983',
    RADIUS: 12,
} as const

/** Figma 4094:59157 — dark espresso hairline, not the champagne used on gold cabins. */
export const FIRST_CLASS_ITINERARY_BORDER = {
    COLOR: '#382411',
    RADIUS: 12,
} as const

export const getItineraryDiscoveryBorder = (theme: ItinHeaderThemeKey): { COLOR: string; RADIUS: number } =>
    theme === 'first' ? FIRST_CLASS_ITINERARY_BORDER : PE_ITINERARY_GOLD_BORDER

/**
 * Addon subtitle copy. Premium Economy / Business / First bundle seats and meals
 * into the fare, so the Economy "cheaper when pre-booked" discount nudge does not
 * apply on those cabins. Baggage keeps the Economy nudge on every cabin.
 */
export const ADDON_SUBTITLE_COPY = {
    ECONOMY: {
        SEAT: 'Seats are cheaper when pre-booked',
        MEAL: 'Meals are cheaper when pre-booked',
    },
    PREMIUM: {
        SEAT: 'Enjoy added comfort with premium seats',
        MEAL: 'Add your meals before you go!',
    },
} as const

export const NUDGE_KIND = {
    PE: 'pe',
    INDIGO: 'indigo',
} as const
